import {
  field,
  date,
  children,
  writer,
  relation,
  text,
} from "@nozbe/watermelondb/decorators";
import { CUSTOMER_SCHEMA, PAYMENT_SCHEMA } from "../schema";
import { Model } from "@nozbe/watermelondb";

export default class Customer extends Model {
  static table = CUSTOMER_SCHEMA;

  static associations = {
    business: { type: "belongs_to", key: "business_id" },
    group_payment_ledger: { type: "has_many", foreignKey: "customer_id" },
    customer_accounts: { type: "has_many", foreignKey: "customer_id" },
    group_customers: { type: "has_many", foreignKey: "customer_id" },
    pos_orders: { type: "has_many", foreignKey: "customer_id" },
    sale_order: { type: "has_many", foreignKey: "customer_id" },
    payment: { type: "has_many", foreignKey: "customer_id" },
    invoice: { type: "has_many", foreignKey: "customer_id" },
    txns: { type: "has_many", foreignKey: "customer_id" },
    cash_txn: { type: "has_many", foreignKey: "customer_id" },
  };

  @field("total_outstanding_payment") total_outstanding_payment;
  @field("transaction_sms") transaction_sms;
  @text("reminder_type") reminder_type;
  @field("is_supplier") is_supplier;
  @field("is_personal") is_personal;
  @field("is_synced") is_synced;
  @text("image_url") image_url;
  @field("readonly") readonly;
  @text("web_link") web_link;
  @text("email") email;
  @text("phone") phone;
  @text("name") name;
  @text("sms") sms;

  @date("last_transaction_at") last_transaction_at;
  @date("collection_set_at") collection_set_at;
  @date("collect_at") collect_at;
  @date("created_at") created_at;
  @date("payment_at") payment_at;
  @date("viewed_at") viewed_at;

  @relation("business", "business_id") business;

  @children("group_payment_ledger") group_payment_ledger;
  @children("customer_accounts") customer_accounts;
  @children("group_customers") group_customers;
  @children("pos_orders") pos_orders;
  @children("sale_order") sale_order;
  @children("payment") payment;
  @children("invoice") invoice;
  @children("txns") txns;
  @children("cash_txn") cash_txn;

  @writer async updatePaymentDate(date) {
    await this.update((customer) => {
      customer.payment_at = date;
      customer.collect_at = null;
    });
  }

  @writer async updateCollectionDate(date) {
    await this.update((customer) => {
      customer.collect_at = date;
      customer.payment_at = null;
    });
  }

  @writer async updatePhoneAndTransactionSMS(details) {
    await this.update((customer) => {
      customer.phone = details.phone;
      customer.transaction_sms = details.transaction_sms;
    });
  }

  @writer async toggleConversion(toggle) {
    return await this.update((customer) => {
      customer.is_supplier = toggle;
    });
  }

  @writer async updateSMS(details) {
    await this.update((customer) => {
      customer.sms = details;
    });
  }

  @writer async updateTotalOutstanding(amount) {
    await this.update((customer) => {
      customer.total_outstanding_payment = amount;
    });
  }

  @writer prepareToUpdateOutstanding(amount) {
    this.prepareUpdate((customer) => {
      customer.total_outstanding_payment = amount;
    });
  }

  @writer async createPayment(details) {
    return await this.batch(
      this.collections.get(PAYMENT_SCHEMA).prepareCreate((payment) => {
        payment.customer.set(this);
        payment.business.set(this.business);
        details.txns && payment.txns.set(details.txns);
        details.invoice && payment.invoice.set(details.invoice);
        details.external_type &&
          (payment.external_type = details.external_type);
        payment._raw.id = details.uid;
        payment.amount = details.amount;
        payment.payment_mode = details.payment_mode;
        payment.image_url = details.imageURL;
        payment.udhaar_note = details.udhaar_note;
        payment.transacted_at = details.date;
        payment.type = details.type;
        payment.attachment = details.attachment;
        payment.category = details.category;
      }),
      this.prepareUpdate((customer) => {
        customer.total_outstanding_payment = details.outstanding;
        customer.last_transaction_at = new Date();
        details.outstanding === 0 && (customer.collect_at = null);
        details.outstanding === 0 && (customer.payment_at = null);
      }),
    );
  }

  @writer async updateCustomer(details) {
    await this.update((customer) => {
      customer.name = details.name;
      customer.phone = details.phone;
      customer.transaction_sms = details.transaction_sms;
      customer.collect_at = details.collect_at ? details.collect_at : null;
      customer.is_supplier =
        typeof details?.is_supplier === "boolean"
          ? details.is_supplier
          : customer.is_supplier;
    });
  }

  @writer async markAsDeleted(deleteInvoice) {
    // deleteInvoice == 'yes' && await this.invoice.destroyAllPermanently()
    // await this.payment.destroyAllPermanently()
    await super.markAsDeleted();
    // await this.destroyPermanently();
  }

  @writer async createPaymentForSync(details) {
    await this.collections.get(PAYMENT_SCHEMA).create((payment) => {
      payment.customer.set(this);
      payment.business.set(this.business);
      details.invoice && payment.invoice.set(details.invoice);
      payment._raw.id = details.uid;
      payment.amount = details.amount;
      payment.payment_mode = details.payment_mode;
      payment.image_url = details.imageURL;
      payment.udhaar_note = details.udhaar_note;
      payment.transacted_at = details.date;
    });
  }
  @writer async updateLastTransactionDate() {
    await this.update((customer) => {
      customer.last_transaction_at = new Date();
    });
  }
}
